import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import StarCard from '@/components/base/StarCard';
import { stars } from '@/mocks/stars';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

export default function Stars() {
  usePageMeta({
    title: '河北彩花明星资料分类 - 明星信息集中展示',
    description:
      '河北彩花及相关演员资料集中展示，可按星座、出生年份与职业身份筛选，快速了解每位演员的基本信息与代表作品。',
    keywords: '河北彩花明星资料,明星资料,演员资料,河北彩花',
  });

  const [constellation, setConstellation] = useState('全部');
  const [birthYear, setBirthYear] = useState('全部');
  const [profession, setProfession] = useState('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const constellationOptions = useMemo(
    () => Array.from(new Set(stars.map((star) => star.constellation))),
    [],
  );
  const birthYearOptions = useMemo(
    () => Array.from(new Set(stars.map((star) => star.birthYear))).sort((a, b) => b - a),
    [],
  );
  const professionOptions = useMemo(
    () => Array.from(new Set(stars.map((star) => star.profession))),
    [],
  );

  const filtered = useMemo(
    () =>
      stars.filter((star) => {
        if (constellation !== '全部' && star.constellation !== constellation) return false;
        if (birthYear !== '全部' && star.birthYear !== Number(birthYear)) return false;
        if (profession !== '全部' && star.profession !== profession) return false;
        return true;
      }),
    [constellation, birthYear, profession],
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [constellation, birthYear, profession]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setConstellation('全部');
    setBirthYear('全部');
    setProfession('全部');
  };

  const filterPanel = (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">星座</h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...constellationOptions].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setConstellation(item)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                constellation === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">出生年份</h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...birthYearOptions.map(String)].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setBirthYear(item)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                birthYear === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {item === '全部' ? '全部' : `${item}年`}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">职业身份</h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...professionOptions].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setProfession(item)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                profession === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={reset}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '明星资料' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            河北彩花明星资料分类 - 明星信息集中展示
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            河北彩花及相关演员的资料集中展示，可按星座、出生年份与职业身份筛选，快速了解每位演员的基本信息与代表作。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex gap-8">
          <aside className="hidden w-[240px] shrink-0 lg:block">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">{filterPanel}</div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                </button>
                <p className="text-sm text-foreground-500">
                  共 <span className="font-semibold text-primary-600">{filtered.length}</span> 位演员
                </p>
              </div>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-3">
                  {shown.map((star) => (
                    <StarCard key={star.id} star={star} />
                  ))}
                </div>

                {visible < filtered.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-add-line"></i>
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-user-unfollow-line text-2xl"></i>
                </div>
                <p className="mt-4 text-base font-medium text-foreground-800">没有找到匹配的明星资料</p>
                <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后再看看</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          ></div>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-[340px] overflow-y-auto bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 位演员
            </button>
          </div>
        </div>
      )}
    </div>
  );
}